import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

export function FlutterHoop({ user, profile, onNavigate }) {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const settingsRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const gameRef = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!gameOver) return;
    setScoreSaved(false);
    supabase.from('flutterhoop_leaderboard').insert([{
      user_id: user.id,
      score: gameRef.current.score,
      username: profile?.username || null,
      email: user.email,
    }]).then(({ error }) => {
      if (error) console.error('Error saving score:', error);
      setScoreSaved(true);
    });
  }, [gameOver, user, profile]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    gameRef.current = {
      started: false,
      over: false,
      score: 0,
      animationId: null,
      tickCount: 0,
      ball: {
        x: 80,
        y: 280,
        vx: 0,
        vy: -2,
        r: 18,
        flapAngle: 0,
        trail: [],
        deathAnim: null,
      },
      hoops: [],
      hoopTimer: 0,
      nextHoop: 90,
      glowHearts: [],
      glowTimer: 0,
      doubleActive: false,
      doubleTimer: 0,
      DOUBLE_DURATION: 600,
      bgOffset: 0,
      particles: [],
      rimFlash: [],
      scorePopups: [],
      rimSlowTimer: 0,   // ticks remaining where hoops move slowly after rim hit
    };

    const ctx = canvas.getContext('2d');
    const game = gameRef.current;

    const GRAVITY = 0.38;
    const BALL_R = 18;
    const FLOOR_Y = canvas.height - 50;
    const HOOP_W = 80;
    const RIM_R = 6;
    const RIM_THICK = 5;
    const HOOP_SPEED_BASE = 2.0;

    // ── Spawn hoop ──────────────────────────────────────────
    const spawnHoop = () => {
      const minY = 120;
      const maxY = FLOOR_Y - 80;
      const y = minY + Math.random() * (maxY - minY);
      game.hoops.push({
        x: canvas.width + 20,
        y,
        scored: false,
        rimTouched: false,
        enteredFromTop: false,
        missed: false,
        speed: HOOP_SPEED_BASE + Math.min(game.score * 0.04, 3),
      });
    };

    // ── Spawn glowing heart ─────────────────────────────────
    const spawnGlowHeart = () => {
      if (game.glowHearts.length > 0) return;
      const y = 100 + Math.random() * (FLOOR_Y - 200);
      game.glowHearts.push({
        x: 80,
        y,
        r: 16,
        collected: false,
        bobOffset: Math.random() * Math.PI * 2,
        pulse: 0,
      });
    };

    // ── Particles ───────────────────────────────────────────
    const spawnParticles = (x, y, color, count = 10) => {
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 1.5 + Math.random() * 3;
        game.particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 2,
          life: 1,
          decay: 0.03 + Math.random() * 0.03,
          r: 3 + Math.random() * 4,
          color,
        });
      }
    };

    // ── Draw helpers ────────────────────────────────────────
    const drawHeartPath = (size) => {
      ctx.moveTo(0, size * 0.4);
      ctx.bezierCurveTo(-size*0.1, size*0.2, -size*0.65, -size*0.1, -size*0.65, -size*0.15);
      ctx.bezierCurveTo(-size*0.65, -size*0.55, -size*0.1, -size*0.65, 0, -size*0.35);
      ctx.bezierCurveTo(size*0.1, -size*0.65, size*0.65, -size*0.55, size*0.65, -size*0.15);
      ctx.bezierCurveTo(size*0.65, -size*0.1, size*0.1, size*0.2, 0, size*0.4);
      ctx.closePath();
    };

    const getZigZag = (size) => [
      { x:  size * 0.05, y: -size * 0.65 },
      { x: -size * 0.12, y: -size * 0.35 },
      { x:  size * 0.1,  y: -size * 0.1  },
      { x: -size * 0.08, y:  size * 0.15 },
      { x:  size * 0.06, y:  size * 0.4  },
    ];

    // ── Draw FlutterHeart character ─────────────────────────
    const drawBall = () => {
      const { x, y, r } = game.ball;
      const BIRD_SIZE = r * 2;
      const heartSize = BIRD_SIZE * 1.3;
      const s = BIRD_SIZE * 0.62 * 0.9; // wing scale

      // Motion trail
      game.ball.trail.forEach((pos, i) => {
        const alpha = (i / game.ball.trail.length) * 0.15;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(pos.x, pos.y);
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(r * (i / game.ball.trail.length) * 0.7);
        ctx.fill();
        ctx.restore();
      });

      const tilt = Math.min(Math.max(game.ball.vy * 0.035, -0.4), 0.7);
      game.ball.flapAngle += 0.14;
      const flap = Math.sin(game.ball.flapAngle) * s * 0.45;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(tilt);

      // Wings
      const drawWing = (side) => {
        const d = side;
        const wGrad = ctx.createLinearGradient(d * s * 0.3, 0, d * s * 2.2, 0);
        wGrad.addColorStop(0, 'rgba(255,220,240,1)');
        wGrad.addColorStop(0.5, 'rgba(255,240,250,0.97)');
        wGrad.addColorStop(1, 'rgba(255,255,255,0.85)');
        ctx.fillStyle = wGrad;
        ctx.beginPath();
        ctx.moveTo(d * s * 0.28, s * 0.05);
        ctx.bezierCurveTo(d * s * 0.9,  -s * 0.35 + flap,
                          d * s * 2.05, -s * 0.15 + flap,
                          d * s * 2.2,   s * 0.28 + flap * 0.3);
        ctx.bezierCurveTo(d * s * 1.7,  s * 0.55,
                          d * s * 0.75, s * 0.38,
                          d * s * 0.28, s * 0.28);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = 'rgba(230,170,210,0.55)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.strokeStyle = 'rgba(210,150,200,0.35)';
        ctx.lineWidth = 0.9;
        [[d*s*2.0, -s*0.1+flap*0.9], [d*s*1.5, -s*0.2+flap*0.7],
         [d*s*1.0, -s*0.25+flap*0.5], [d*s*0.6, -s*0.18+flap*0.3]].forEach(([fx, fy]) => {
          ctx.beginPath();
          ctx.moveTo(d * s * 0.28, s * 0.18);
          ctx.quadraticCurveTo(d * s * 0.5, fy * 0.5, fx, fy);
          ctx.stroke();
        });
      };

      if (!game.ball.deathAnim) {
        drawWing(-1);
        drawWing(1);
      }

      // Heart body — normal or death split animation
      if (!game.ball.deathAnim) {
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
      } else {
        const t = game.ball.deathAnim.frame / 40;
        const spread = t * s * 2.5;
        const drop   = t * t * s * 3;
        const spin   = t * 1.0;
        const alpha  = Math.max(0, 1 - t);
        const zz     = getZigZag(heartSize);
        ctx.globalAlpha = alpha;

        const leftClip = () => {
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(-heartSize * 2, zz[zz.length - 1].y);
          ctx.lineTo(-heartSize * 2, -heartSize * 2);
          ctx.lineTo(zz[0].x, -heartSize * 2);
          ctx.closePath();
          ctx.clip();
        };
        const rightClip = () => {
          ctx.beginPath();
          ctx.moveTo(zz[0].x, zz[0].y);
          zz.forEach(p => ctx.lineTo(p.x, p.y));
          ctx.lineTo(heartSize * 2, zz[zz.length - 1].y);
          ctx.lineTo(heartSize * 2, -heartSize * 2);
          ctx.lineTo(zz[0].x, -heartSize * 2);
          ctx.closePath();
          ctx.clip();
        };

        ctx.save();
        ctx.translate(-spread, drop);
        ctx.rotate(-spin);
        leftClip();
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.translate(spread, drop);
        ctx.rotate(spin);
        rightClip();
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        drawHeartPath(heartSize);
        ctx.fill();
        ctx.restore();

        ctx.globalAlpha = 1;
      }

      ctx.restore();

      // Shockwave ring on death
      if (game.ball.deathAnim && game.ball.deathAnim.frame < 20) {
        const t = game.ball.deathAnim.frame / 20;
        ctx.save();
        ctx.translate(x, y);
        ctx.strokeStyle = `rgba(244,63,94,${1 - t})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, t * BALL_R * 4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    };

    const drawHoop = (hoop) => {
      const { x, y } = hoop;

      const flash = game.rimFlash.find(f => f.hoop === hoop);
      if (flash) {
        ctx.shadowColor = flash.clean ? '#fbbf24' : '#f97316';
        ctx.shadowBlur = 22 * flash.life;
      }

      const rimColorDark  = hoop.rimTouched ? '#c2410c' : '#b45309';
      const rimColorLight = hoop.rimTouched ? '#fb923c' : '#fcd34d';
      const rx = HOOP_W / 2 + RIM_R;
      const ry = RIM_R * 1.8;

      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, Math.PI, Math.PI * 2);
      ctx.strokeStyle = rimColorDark;
      ctx.lineWidth = RIM_THICK + 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI);
      ctx.strokeStyle = rimColorLight;
      ctx.lineWidth = RIM_THICK + 2;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(x, y, rx, ry, 0, Math.PI * 0.1, Math.PI * 0.9);
      ctx.strokeStyle = '#fef3c7';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.shadowBlur = 0;
    };

    const drawGlowHeart = (gh) => {
      if (gh.collected) return;
      const bob = Math.sin(game.tickCount * 0.07 + gh.bobOffset) * 5;
      gh.pulse = (gh.pulse + 0.08) % (Math.PI * 2);
      const scale = 1 + Math.sin(gh.pulse) * 0.12;

      ctx.save();
      ctx.translate(gh.x, gh.y + bob);
      ctx.scale(scale, scale);
      ctx.shadowColor = '#fb7185';
      ctx.shadowBlur = 18 + Math.sin(gh.pulse) * 8;
      ctx.fillStyle = '#fda4af';
      ctx.beginPath();
      drawHeartPath(gh.r * 1.4);
      ctx.fill();
      ctx.shadowBlur = 6;
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      drawHeartPath(gh.r * 0.9);
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.restore();

      ctx.fillStyle = 'rgba(244,63,94,0.85)';
      ctx.font = 'bold 10px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('2×', gh.x, gh.y + bob + gh.r + 12);
      ctx.textAlign = 'left';
    };

    const drawParticles = () => {
      game.particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace(')', `,${p.life})`).replace('rgb', 'rgba');
        ctx.fill();
      });
    };

    const drawScorePopups = () => {
      game.scorePopups.forEach(sp => {
        ctx.globalAlpha = sp.life;
        ctx.fillStyle = sp.color || '#f43f5e';
        ctx.font = `bold ${sp.size || 20}px Inter, system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(sp.text, sp.x, sp.y);
        ctx.textAlign = 'left';
        ctx.globalAlpha = 1;
      });
    };

    // ── Render ──────────────────────────────────────────────
    const render = () => {
      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, '#fce4f5');
      grad.addColorStop(1, '#fef0ff');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#f9a8d4';
      ctx.fillRect(0, FLOOR_Y, canvas.width, canvas.height - FLOOR_Y);
      ctx.fillStyle = '#f472b6';
      ctx.fillRect(0, FLOOR_Y, canvas.width, 5);

      game.bgOffset = (game.bgOffset + (game.started ? HOOP_SPEED_BASE : 0)) % 48;
      ctx.fillStyle = '#fda4af';
      for (let bx = -game.bgOffset; bx < canvas.width; bx += 48) {
        ctx.fillRect(bx, FLOOR_Y + 16, 28, 4);
      }

      game.hoops.forEach(h => drawHoop(h));
      game.glowHearts.forEach(gh => drawGlowHeart(gh));
      drawBall();
      drawParticles();
      drawScorePopups();

      // HUD score
      ctx.fillStyle = 'rgba(180,80,120,0.8)';
      ctx.font = 'bold 22px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(game.score, canvas.width / 2, 36);
      ctx.textAlign = 'left';

      // 2x badge
      if (game.doubleActive) {
        const remaining = game.doubleTimer / game.DOUBLE_DURATION;
        ctx.fillStyle = 'rgba(251,191,36,0.92)';
        ctx.beginPath();
        ctx.roundRect(canvas.width - 72, 14, 58, 26, 13);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.beginPath();
        ctx.roundRect(canvas.width - 70, 16, 54 * remaining, 22, 11);
        ctx.fill();
        ctx.fillStyle = '#7c2d12';
        ctx.font = 'bold 13px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('2× ON', canvas.width - 43, 31);
        ctx.textAlign = 'left';
      }

      if (!game.started) {
        ctx.fillStyle = 'rgba(255,240,250,0.72)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#a03070';
        ctx.font = '600 16px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Tap or press Space to flap', canvas.width / 2, canvas.height / 2 - 12);
        ctx.font = '400 13px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#c060a0';
        ctx.fillText('Fall through the hoop — clean = 2× pts!', canvas.width / 2, canvas.height / 2 + 14);
        ctx.textAlign = 'left';
      }
    };

    // ── Physics helpers ─────────────────────────────────────
    const checkRimCollision = (hoop) => {
      if (hoop.scored) return false;
      const ball = game.ball;
      const leftX  = hoop.x - HOOP_W / 2;
      const rightX = hoop.x + HOOP_W / 2;

      let hit = false;
      [[leftX, hoop.y, 'left'], [rightX, hoop.y, 'right']].forEach(([rx, ry, side]) => {
        const dx = ball.x - rx;
        const dy = ball.y - ry;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < ball.r + RIM_R && dist > 0) {
          const nx = dx / dist;
          const ny = dy / dist;
          const dot = ball.vx * nx + ball.vy * ny;
          const fromAbove = ball.y <= ry + RIM_R * 0.5;

          if (dot < 0) {
            if (fromAbove) {
              // Lively bounce — enough energy that the heart visibly bounces
              const restitution = 0.62;
              ball.vx = (ball.vx - 2 * dot * nx) * restitution;
              ball.vy = (ball.vy - 2 * dot * ny) * restitution;
              if (ball.vy < 0) ball.vy *= 0.5;
            } else {
              const restitution = 0.7;
              ball.vx = (ball.vx - 2 * dot * nx) * restitution;
              ball.vy = (ball.vy - 2 * dot * ny) * restitution;
            }
          }

          const overlap = ball.r + RIM_R - dist;
          ball.x += nx * overlap;
          ball.y += ny * overlap;
          hit = true;
          hoop.rimTouched = true;
          // Slow the board for 40 ticks so the heart has room to bounce and settle
          game.rimSlowTimer = Math.max(game.rimSlowTimer, 60);
        }
      });
      return hit;
    };

    const checkScore = (hoop) => {
      if (hoop.scored) return false;
      const ball = game.ball;

      const withinGapX = Math.abs(ball.x - hoop.x) < HOOP_W / 2 - ball.r * 0.3;
      if (withinGapX && ball.y < hoop.y && ball.vy > 0) {
        hoop.enteredFromTop = true;
      }

      const fullyThrough = ball.y > hoop.y + ball.r * 0.5;
      const movingDown   = ball.vy > 0;

      if (hoop.enteredFromTop && withinGapX && fullyThrough && movingDown) {
        hoop.scored = true;
        ball.vx = 0;
        const clean = !hoop.rimTouched;
        const base = clean ? 2 : 1;
        const multiplier = game.doubleActive ? 2 : 1;
        const pts = base * multiplier;
        game.score += pts;
        setScore(game.score);

        spawnParticles(ball.x, ball.y, clean ? 'rgb(251,191,36)' : 'rgb(244,63,94)', 14);
        game.rimFlash.push({ hoop, life: 1, clean });

        const label = clean
          ? (multiplier === 2 ? '+4 🔥 CLEAN!' : '+2 CLEAN!')
          : (multiplier === 2 ? '+2' : '+1');
        game.scorePopups.push({
          x: hoop.x, y: hoop.y - 20,
          text: label, life: 1,
          color: clean ? '#f59e0b' : '#f43f5e',
          size: clean ? 22 : 18,
        });
        return true;
      }
      return false;
    };

    // ── Update ──────────────────────────────────────────────
    const update = () => {
      if (!game.started || game.over) return;
      game.tickCount++;
      const ball = game.ball;

      ball.trail.push({ x: ball.x, y: ball.y });
      if (ball.trail.length > 8) ball.trail.shift();

      ball.vy += GRAVITY;
      ball.y += ball.vy;
      ball.x += ball.vx;
      // Slower vx dampen so rim deflections actually travel before settling
      ball.vx *= 0.96;
      ball.x = Math.max(20, Math.min(canvas.width - 20, ball.x));

      if (ball.y + ball.r >= FLOOR_Y) {
        game.over = true;
        ball.deathAnim = { frame: 0 };
        return;
      }
      if (ball.y - ball.r <= 0) {
        game.over = true;
        ball.deathAnim = { frame: 0 };
        return;
      }

      // Tick rim slow timer down
      if (game.rimSlowTimer > 0) game.rimSlowTimer--;
      // Speed multiplier: starts at 0.2 right after hit, ramps back to 1.0 as timer falls
      const speedMult = game.rimSlowTimer > 0
        ? 0.2 + 0.8 * (1 - game.rimSlowTimer / 60)
        : 1.0;

      game.hoops.forEach(h => {
        h.x -= h.speed * speedMult;
        if (!h.scored) {
          checkRimCollision(h);
          checkScore(h);
          if (!h.missed && (h.x + HOOP_W / 2 + RIM_R) < ball.x - ball.r) {
            h.missed = true;
            game.over = true;
            ball.deathAnim = { frame: 0 };
          }
        }
      });
      game.hoops = game.hoops.filter(h => !h.scored && h.x + HOOP_W > -20);

      game.hoopTimer++;
      if (game.hoopTimer >= game.nextHoop) {
        spawnHoop();
        game.hoopTimer = 0;
        game.nextHoop = 70 + Math.floor(Math.random() * 60);
      }

      game.glowHearts.forEach(gh => {
        if (!gh.collected) {
          const dx = ball.x - gh.x;
          const dy = ball.y - gh.y;
          if (Math.sqrt(dx*dx + dy*dy) < ball.r + gh.r) {
            gh.collected = true;
            game.doubleActive = true;
            game.doubleTimer = game.DOUBLE_DURATION;
            spawnParticles(gh.x, gh.y, 'rgb(251,113,133)', 18);
            game.scorePopups.push({
              x: gh.x, y: gh.y - 16,
              text: '2× ACTIVE! 🩷', life: 1,
              color: '#f43f5e', size: 20,
            });
          }
        }
      });
      game.glowHearts = game.glowHearts.filter(gh => !gh.collected);

      game.glowTimer++;
      if (game.glowTimer > 220 + Math.floor(Math.random() * 180)) {
        if (Math.random() < 0.6) spawnGlowHeart();
        game.glowTimer = 0;
      }

      if (game.doubleActive) {
        game.doubleTimer--;
        if (game.doubleTimer <= 0) {
          game.doubleActive = false;
          game.doubleTimer = 0;
        }
      }

      game.particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        p.vy += 0.1; p.life -= p.decay;
      });
      game.particles = game.particles.filter(p => p.life > 0);

      game.scorePopups.forEach(sp => { sp.y -= 1.2; sp.life -= 0.025; });
      game.scorePopups = game.scorePopups.filter(sp => sp.life > 0);

      game.rimFlash.forEach(f => { f.life -= 0.06; });
      game.rimFlash = game.rimFlash.filter(f => f.life > 0);
    };

    const DEATH_FRAMES = 40;

    const gameLoop = () => {
      update();
      render();
      if (!game.over) {
        game.animationId = requestAnimationFrame(gameLoop);
      } else if (game.ball.deathAnim && game.ball.deathAnim.frame <= DEATH_FRAMES) {
        game.ball.deathAnim.frame++;
        render();
        if (game.ball.deathAnim.frame <= DEATH_FRAMES) {
          game.animationId = requestAnimationFrame(gameLoop);
        } else {
          setGameOver(true);
        }
      } else {
        setGameOver(true);
      }
    };

    const drop = () => {
      if (!game.started) {
        game.started = true;
        spawnHoop();
        return;
      }
      if (!game.over) {
        game.ball.vy = -8;
      }
    };

    const handleKeyDown = (e) => { if (e.code === 'Space') { e.preventDefault(); drop(); } };
    const handleClick = () => drop();
    const handleTouch = (e) => { e.preventDefault(); drop(); };

    render();
    gameLoop();
    window.addEventListener('keydown', handleKeyDown);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouch, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouch);
      cancelAnimationFrame(game.animationId);
    };
  }, [restartTrigger]);

  const handlePlayAgain = () => {
    setScore(0);
    setGameOver(false);
    setRestartTrigger(t => t + 1);
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <div className="settings-menu" ref={settingsRef}>
          <button className="settings-btn" onClick={() => setSettingsOpen(v => !v)} aria-label="Settings">⚙️</button>
          {settingsOpen && (
            <div className="settings-dropdown">
              <div className="settings-email">{user.email}</div>
              <hr className="settings-divider" />
              <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('edit-profile'); }}>Edit Username</button>
              {profile?.is_admin && (
                <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('admin'); }}>Admin Panel</button>
              )}
              <hr className="settings-divider" />
              <button className="settings-item settings-item-danger" onClick={() => supabase.auth.signOut()}>Logout</button>
            </div>
          )}
        </div>
        <span className="game-username">{profile?.username || user.email}</span>
        <button className="btn back-btn" onClick={() => navigate('/')}>← Games</button>
      </div>

      <h1 className="game-title">🩷 Flutter Hoop</h1>
      <canvas ref={canvasRef} width={500} height={600} className="game-canvas" />

      <button className="btn leaderboard-btn" onClick={() => onNavigate('leaderboard')}>
        🏆 Leaderboard
      </button>
      <button className="btn leaderboard-btn" onClick={() => onNavigate('leagues')}>
        🏅 Leagues
      </button>

      {gameOver && (
        <div className="game-over-modal">
          <div className="game-over-box">
            <h2>Game Over!</h2>
            <p className="final-score">{score}</p>
            <button onClick={handlePlayAgain} className="btn">Play Again</button>
            <button onClick={() => onNavigate('leaderboard')} className="btn btn-submit" disabled={!scoreSaved}>
              {scoreSaved ? 'View Leaderboard' : 'Saving…'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}